import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

const verificationErrorMessage = (detail?: string): string => {
  switch (detail) {
    case 'verification_expired':
      return '認証コードの有効期限が切れています。作成者に認証メールの再送を依頼してください。';
    case 'invalid_verification_code':
      return '認証コードが正しくありません。';
    case 'verification_attempts_exceeded':
      return '認証コードの入力上限に達しました。作成者に認証メールの再送を依頼してください。';
    case 'verification_rate_limited':
      return '認証の試行回数が上限に達しました。しばらく時間をおいてください。';
    case 'invalid_verification_challenge':
    case 'viewer_already_activated':
      return '認証リンクが無効です。作成者に認証メールの再送を依頼してください。';
    default:
      return '本人確認に失敗しました。時間をおいて再度お試しください。';
  }
};

const ViewerActivation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const challengeId = searchParams.get('challengeId')?.trim() ?? '';
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActivated, setIsActivated] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!challengeId) {
      setError('認証リンクが無効です。作成者に認証メールの再送を依頼してください。');
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError('6桁の認証コードを入力してください。');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/viewer-activation/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ challengeId, code }),
      });
      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        setError(verificationErrorMessage(responseData?.detail));
        return;
      }

      setIsActivated(true);
    } catch {
      setError('本人確認に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          閲覧専用ユーザーの本人確認
        </Typography>

        {isActivated ? (
          <>
            <Alert severity="success" sx={{ my: 3 }}>
              本人確認が完了しました。ログインしてください。
            </Alert>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login', { replace: true })}
            >
              ログイン画面へ
            </Button>
          </>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {!challengeId && (
              <Alert severity="error" sx={{ my: 3 }}>
                認証リンクが無効です。作成者に認証メールの再送を依頼してください。
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ my: 3 }}>
                {error}
              </Alert>
            )}

            <Typography color="text.secondary" sx={{ mt: 2 }}>
              メールに記載された6桁の認証コードを入力してください。コードの有効期限は60分です。
            </Typography>

            <TextField
              margin="normal"
              required
              fullWidth
              autoFocus
              id="viewerActivationCode"
              name="viewerActivationCode"
              label="6桁の認証コード"
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                setError('');
              }}
              disabled={isSubmitting || !challengeId}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 6,
                autoComplete: 'one-time-code',
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, py: 1.5 }}
              disabled={isSubmitting || !challengeId || code.length !== 6}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                '本人確認を完了する'
              )}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ViewerActivation;
